import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Table,
  Input,
  Breadcrumb,
  Tag,
  DatePicker,
  Row,
  Col,
  Grid,
  Select,
} from 'antd';
import { Link } from 'react-router-dom';
import { FileExcelOutlined, FilePdfOutlined, DownloadOutlined, RightOutlined, EnvironmentOutlined } from '@ant-design/icons';
import moment from 'moment';
import 'moment/locale/fr';
import * as XLSX from 'xlsx';
// xlsx-js-style : fork de xlsx qui supporte les styles (bordures, fonts, couleurs).
// API identique à xlsx, donc on l'utilise uniquement pour l'export .xlsx stylé.
import XLSXStyle from 'xlsx-js-style';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { dashboardsService, citiesService } from '../../common/services';

const { RangePicker } = DatePicker;
const { Option } = Select;

const TYPE_LABEL = {
  sos_credit: 'SOS Crédit',
  transfert: 'Transfert',
  changement_numero: 'Chgt de numéro',
  promo_dimanche: 'Promo dimanche',
  vehicule_modifie: 'Véhicule modifié',
};

const TYPE_COLOR = {
  sos_credit: 'geekblue',
  transfert: 'purple',
  changement_numero: 'orange',
  promo_dimanche: 'red',
  vehicule_modifie: 'green',
};

const Operations = () => {
  const [rows, setRows] = useState([]);
  const [isLoading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  // filtres
  const [searchTerm, setSearchTerm] = useState('');
  const [types, setTypes] = useState([]); // ['sos_credit','transfert',...]
  const [dateRange, setDateRange] = useState(null); // [moment, moment]
  const [cityFilter, setCityFilter] = useState(undefined);
  const [cities, setCities] = useState([]);

  // pagination serveur
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [total, setTotal] = useState(0);

  // breakpoints pour responsive
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.sm; // < 576px
  const controlSize = isMobile ? 'small' : 'middle';

  // fetch
  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {
        page,
        limit,
        q: searchTerm || undefined,
        types: types.join(',') || undefined,
        start: dateRange?.[0] ? dateRange[0].startOf('day').toISOString() : undefined,
        end: dateRange?.[1] ? dateRange[1].endOf('day').toISOString() : undefined,
        cityCode: cityFilter || undefined,
        sort: '-date',
      };
      const res = await dashboardsService.listAllOperations(params);
      setRows(res.items || []);
      setTotal(res.total || 0);
      setPage(res.page || 1);
      setLimit(res.limit || 20);
    } catch (err) {
      console.error('Erreur API operations/list:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit]); // fetch initial & quand pagination change

  // re-fetch quand filtres changent (et reset page)
  useEffect(() => {
    setPage(1);
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, types, dateRange, cityFilter]);

  // Charger les villes (pour le filtre)
  useEffect(() => {
    (async () => {
      try {
        const res = await citiesService.getAll();
        const list = Array.isArray(res)
          ? res
          : (res?.data || res?.items || res?.cities || []);
        setCities(list.filter(c => c?.isActive !== false));
      } catch (e) {
        console.error('Erreur chargement villes:', e);
      }
    })();
  }, []);

  // colonnes du tableau
  const columns = useMemo(
    () => [
      {
        title: 'Type',
        dataIndex: 'type',
        key: 'type',
        render: (t) => <Tag color={TYPE_COLOR[t] || 'default'}>{TYPE_LABEL[t] || t}</Tag>,
        filters: Object.keys(TYPE_LABEL).map((k) => ({ text: TYPE_LABEL[k], value: k })),
        filteredValue: types.length ? types : null,
        onFilter: () => true, // visuel uniquement (filtrage serveur)
        responsive: ['xs', 'sm', 'md', 'lg', 'xl'],
      },
      {
        title: 'Ville',
        key: 'ville',
        width: 130,
        render: (_, r) => {
          const nom = r.ville?.nom;
          if (!nom) return <span style={{ color: '#bfbfbf' }}>—</span>;
          return (
            <Tag color="geekblue" icon={<EnvironmentOutlined />}>{nom}</Tag>
          );
        },
        responsive: ['sm', 'md', 'lg', 'xl'],
      },
      {
        title: 'Date',
        dataIndex: 'date',
        key: 'date',
        render: (d) => moment(d).format('DD/MM/YYYY HH:mm:ss'),
        sorter: (a, b) => new Date(a.date) - new Date(b.date),
        defaultSortOrder: 'descend',
        responsive: ['xs', 'sm', 'md', 'lg', 'xl'],
      },
      {
        title: 'Montant',
        dataIndex: 'amount',
        key: 'amount',
        render: (v, r) =>
          r.type === 'sos_credit' || r.type === 'transfert'
            ? `${(v ?? 0).toLocaleString('fr-FR')}`
            : '—',
        align: 'right',
        responsive: ['sm', 'md', 'lg', 'xl'], // on peut masquer sur très petit écran si besoin
      },
      {
        title: 'Contractor / Parties',
        key: 'who',
        render: (_, r) => {
          if (r.type === 'transfert') {
            return (
              <div>
                <div>
                  <strong>De:</strong> {r.senderPhone || r.senderId || '—'}
                </div>
                <div>
                  <strong>À:</strong> {r.receiverPhone || r.receiverId || '—'}
                </div>
              </div>
            );
          }
          if (r.type === 'sos_credit') {
            return (
              <div>
                <div>
                  <strong>Contractor:</strong> {r.contractorId || '—'}
                </div>
                <div>
                  <strong>Téléphone:</strong> {r.phone || '—'}
                </div>
              </div>
            );
          }
          if (r.type === 'changement_numero') {
            return (
              <div>
                <div>
                  <strong>Ancien:</strong> {r.meta?.ancienNumero || '—'}
                </div>
                <div>
                  <strong>Nouveau:</strong> {r.meta?.nouveauNumero || '—'}
                </div>
              </div>
            );
          }
          if (r.type === 'vehicule_modifie') {
            return (
              <div>
                <div>
                  <strong>Immatriculation:</strong> {r.meta?.ancienneImmatriculation} → {r.meta?.nouvelleImmatriculation}
                </div>
                <div>
                  <strong>Tél.:</strong> {r.phone || '—'}
                </div>
              </div>
            );
          }
          if (r.type === 'promo_dimanche') {
            return (
              <div>
                <strong>Contractor:</strong> {r.contractorId || '—'}
              </div>
            );
          }
          return '—';
        },
        responsive: ['xs', 'sm', 'md', 'lg', 'xl'],
      },
    ],
    [types]
  );

  // pagination Antd -> serveur
  const onChangeTable = (pagination /*, filters, sorter*/) => {
    setPage(pagination.current);
    setLimit(pagination.pageSize);
  };

  // Récupère TOUTES les opérations matching les filtres courants (pas seulement la page courante).
  // Limite haute à 100k pour rester safe ; si un jour on dépasse, paginer côté export.
  const fetchAllForExport = async () => {
    const params = {
      page: 1,
      limit: 100000,
      q: searchTerm || undefined,
      types: types.join(',') || undefined,
      start: dateRange?.[0] ? dateRange[0].startOf('day').toISOString() : undefined,
      end: dateRange?.[1] ? dateRange[1].endOf('day').toISOString() : undefined,
      cityCode: cityFilter || undefined,
      sort: '-date',
    };
    const res = await dashboardsService.listAllOperations(params);
    return res.items || [];
  };

  // Construit la liste des lignes "meta" affichées au-dessus du tableau (titre, total,
  // ville filtrée, période, etc.). Réutilisable pour CSV et toute future intégration.
  // Renvoie un tableau de chaînes (1 string = 1 ligne du fichier).
  const buildMetaLines = (totalCount) => {
    const lines = ['Liste des opérations', `Total : ${totalCount} opérations`];
    if (cityFilter) {
      const ville = cities.find((c) => c.code === cityFilter)?.name || cityFilter;
      lines.push(`Ville : ${ville}`);
    }
    if (dateRange?.[0] && dateRange?.[1]) {
      lines.push(`Période : ${dateRange[0].format('DD/MM/YYYY')} - ${dateRange[1].format('DD/MM/YYYY')}`);
    }
    if (types.length) {
      lines.push(`Types : ${types.map((t) => TYPE_LABEL[t] || t).join(', ')}`);
    }
    if (searchTerm) {
      lines.push(`Recherche : "${searchTerm}"`);
    }
    lines.push(`Généré le ${moment().format('DD/MM/YYYY HH:mm')}`);
    return lines;
  };

  // Mapping pour CSV (toutes colonnes utiles, sans ContractorId).
  const mapForSheet = (items) => items.map((r) => ({
    Type: TYPE_LABEL[r.type] || r.type,
    Ville: r.ville?.nom ?? '',
    Date: moment(r.date).format('DD/MM/YYYY HH:mm:ss'),
    Montant: (r.amount ?? '').toString(),
    Téléphone: r.phone ?? '',
    SenderId: r.senderId ?? '',
    ReceiverId: r.receiverId ?? '',
    SenderPhone: r.senderPhone ?? '',
    ReceiverPhone: r.receiverPhone ?? '',
    AncienneImmatriculation: r.meta?.ancienneImmatriculation ?? '',
    NouvelleImmatriculation: r.meta?.nouvelleImmatriculation ?? '',
    AncienNumero: r.meta?.ancienNumero ?? '',
    NouveauNumero: r.meta?.nouveauNumero ?? '',
  }));

  const exportToExcel = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const all = await fetchAllForExport();

      // Headers du tableau (mêmes colonnes que le mapping CSV/JSON, sans ContractorId).
      const HEADERS = [
        'Type', 'Ville', 'Date', 'Montant',
        'Téléphone',
        'SenderId', 'ReceiverId', 'SenderPhone', 'ReceiverPhone',
        'Ancienne Immat.', 'Nouvelle Immat.', 'Ancien Num.', 'Nouveau Num.',
      ];
      const NUM_COLS = HEADERS.length;

      // Styles réutilisables
      const thin = { style: 'thin', color: { rgb: '999999' } };
      const fullBorder = { top: thin, bottom: thin, left: thin, right: thin };

      const titleStyle = {
        font: { bold: true, sz: 16, color: { rgb: '000000' } },
        alignment: { horizontal: 'center', vertical: 'center' },
      };
      const subtitleStyle = {
        font: { sz: 11, color: { rgb: '555555' } },
        alignment: { horizontal: 'center', vertical: 'center' },
      };
      const subtitleRedStyle = {
        font: { sz: 11, bold: true, color: { rgb: 'C81E1E' } }, // rouge
        alignment: { horizontal: 'center', vertical: 'center' },
      };
      const generatedStyle = {
        font: { sz: 9, italic: true, color: { rgb: '888888' } },
        alignment: { horizontal: 'center', vertical: 'center' },
      };
      const headerStyle = {
        font: { bold: true, color: { rgb: 'FFFFFF' }, sz: 11 },
        fill: { fgColor: { rgb: '1976D2' } },
        alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
        border: fullBorder,
      };
      const cellStyle = {
        font: { sz: 10, color: { rgb: '222222' } },
        border: fullBorder,
        alignment: { vertical: 'center', wrapText: false },
      };
      const cellRightStyle = { ...cellStyle, alignment: { ...cellStyle.alignment, horizontal: 'right' } };

      // ---- Construction des lignes (Array of Arrays) ----
      const aoa = [];

      // Ligne 0 : titre fusionné
      aoa.push([{ v: 'Liste des opérations', s: titleStyle }]);

      // Ligne 1 : Total
      aoa.push([{ v: `Total : ${all.length} opérations`, s: subtitleStyle }]);

      // Ligne 2 (conditionnelle) : Ville en rouge
      if (cityFilter) {
        const ville = cities.find((c) => c.code === cityFilter)?.name || cityFilter;
        aoa.push([{ v: `Ville : ${ville}`, s: subtitleRedStyle }]);
      }

      // Ligne suivante (conditionnelle) : Période en rouge
      if (dateRange?.[0] && dateRange?.[1]) {
        const periode = `Période : ${dateRange[0].format('DD/MM/YYYY')} - ${dateRange[1].format('DD/MM/YYYY')}`;
        aoa.push([{ v: periode, s: subtitleRedStyle }]);
      }

      // Ligne suivante (conditionnelle) : Types — en rouge comme Ville et Période
      if (types.length) {
        aoa.push([{ v: `Types : ${types.map((t) => TYPE_LABEL[t] || t).join(', ')}`, s: subtitleRedStyle }]);
      }

      // Ligne suivante (conditionnelle) : Recherche
      if (searchTerm) {
        aoa.push([{ v: `Recherche : "${searchTerm}"`, s: subtitleStyle }]);
      }

      // Ligne suivante : généré le
      aoa.push([{ v: `Généré le ${moment().format('DD/MM/YYYY HH:mm')}`, s: generatedStyle }]);

      // Ligne vide entre l'en-tête et le tableau
      aoa.push([]);

      // Index où commence le tableau (pour les merges sur les lignes meta).
      const headerRowIdx = aoa.length;

      // Ligne header du tableau
      aoa.push(HEADERS.map((h) => ({ v: h, s: headerStyle })));

      // Lignes data — toutes encadrées + cumul du total des montants
      let totalMontant = 0;
      let countMontant = 0;
      for (const r of all) {
        if (typeof r.amount === 'number' && Number.isFinite(r.amount)) {
          totalMontant += r.amount;
          countMontant += 1;
        }
        aoa.push([
          { v: TYPE_LABEL[r.type] || r.type || '', s: cellStyle },
          { v: r.ville?.nom ?? '', s: cellStyle },
          { v: moment(r.date).format('DD/MM/YYYY HH:mm:ss'), s: cellStyle },
          { v: r.amount ?? '', t: r.amount != null ? 'n' : 's', s: cellRightStyle },
          { v: r.phone ?? '', s: cellStyle },
          { v: r.senderId ?? '', s: cellStyle },
          { v: r.receiverId ?? '', s: cellStyle },
          { v: r.senderPhone ?? '', s: cellStyle },
          { v: r.receiverPhone ?? '', s: cellStyle },
          { v: r.meta?.ancienneImmatriculation ?? '', s: cellStyle },
          { v: r.meta?.nouvelleImmatriculation ?? '', s: cellStyle },
          { v: r.meta?.ancienNumero ?? '', s: cellStyle },
          { v: r.meta?.nouveauNumero ?? '', s: cellStyle },
        ]);
      }

      // Ligne TOTAL en bas : libellé fusionné sur les 3 premières colonnes,
      // puis valeur du total dans la colonne Montant (avec format milliers).
      const totalLabelStyle = {
        font: { bold: true, sz: 12, color: { rgb: 'FFFFFF' } },
        fill: { fgColor: { rgb: '0D47A1' } }, // bleu foncé
        alignment: { horizontal: 'right', vertical: 'center' },
        border: fullBorder,
      };
      const totalValueStyle = {
        font: { bold: true, sz: 12, color: { rgb: 'FFFFFF' } },
        fill: { fgColor: { rgb: '0D47A1' } },
        alignment: { horizontal: 'right', vertical: 'center' },
        border: fullBorder,
        numFmt: '#,##0" FCFA"', // formatage devise français
      };
      const totalEmptyStyle = {
        fill: { fgColor: { rgb: '0D47A1' } },
        border: fullBorder,
      };
      const totalRow = [
        { v: `TOTAL  (${countMontant} opération${countMontant > 1 ? 's' : ''} avec montant)`, s: totalLabelStyle },
        { v: '', s: totalEmptyStyle },
        { v: '', s: totalEmptyStyle },
        { v: totalMontant, t: 'n', s: totalValueStyle },
      ];
      // Compléter avec des cellules vides stylées pour conserver les bordures sur toute la largeur
      while (totalRow.length < NUM_COLS) {
        totalRow.push({ v: '', s: totalEmptyStyle });
      }
      aoa.push(totalRow);

      const ws = XLSXStyle.utils.aoa_to_sheet(aoa);

      // Mémoriser la position de la ligne TOTAL (dernière ligne ajoutée)
      const totalRowIdx = aoa.length - 1;

      // Fusionner toutes les lignes meta (titre, total, ville, période, etc.) sur la largeur du tableau.
      ws['!merges'] = [];
      for (let r = 0; r < headerRowIdx - 1; r += 1) { // -1 pour exclure la ligne vide
        ws['!merges'].push({ s: { r, c: 0 }, e: { r, c: NUM_COLS - 1 } });
      }
      // Fusionner les 3 premières cellules de la ligne TOTAL (Type, Ville, Date) pour le libellé
      ws['!merges'].push({ s: { r: totalRowIdx, c: 0 }, e: { r: totalRowIdx, c: 2 } });

      // Hauteurs : titre plus grand
      ws['!rows'] = [];
      ws['!rows'][0] = { hpt: 26 };                  // titre
      for (let r = 1; r < headerRowIdx - 1; r += 1) {
        ws['!rows'][r] = { hpt: 18 };                // sous-titres
      }
      ws['!rows'][headerRowIdx] = { hpt: 22 };       // header tableau
      ws['!rows'][totalRowIdx] = { hpt: 24 };        // ligne TOTAL un peu plus haute

      // Largeurs de colonnes (en "char widths" — 1 wch ≈ 7 px)
      ws['!cols'] = [
        { wch: 16 }, // Type
        { wch: 16 }, // Ville
        { wch: 20 }, // Date
        { wch: 12 }, // Montant
        { wch: 18 }, // Téléphone
        { wch: 24 }, // SenderId
        { wch: 24 }, // ReceiverId
        { wch: 18 }, // SenderPhone
        { wch: 18 }, // ReceiverPhone
        { wch: 18 }, // Ancienne Immat.
        { wch: 18 }, // Nouvelle Immat.
        { wch: 14 }, // Ancien Num.
        { wch: 14 }, // Nouveau Num.
      ];

      const wb = XLSXStyle.utils.book_new();
      XLSXStyle.utils.book_append_sheet(wb, ws, 'Opérations');
      XLSXStyle.writeFile(wb, `operations-${moment().format('YYYY-MM-DD')}.xlsx`);
    } catch (e) {
      console.error('Erreur export Excel:', e);
    } finally {
      setExporting(false);
    }
  };

  const exportToCSV = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const all = await fetchAllForExport();
      const ws = XLSX.utils.json_to_sheet(mapForSheet(all));
      const csvData = XLSX.utils.sheet_to_csv(ws);

      // Calcul du total des montants (ignore les opérations sans montant)
      let totalMontant = 0;
      let countMontant = 0;
      for (const r of all) {
        if (typeof r.amount === 'number' && Number.isFinite(r.amount)) {
          totalMontant += r.amount;
          countMontant += 1;
        }
      }

      // Lignes meta (titre + filtres) en tête du CSV. Échappées : si une ligne contient
      // des virgules, on l'entoure de guillemets pour ne pas créer de fausses colonnes.
      const escape = (s) => (/[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s);
      const metaCsv = buildMetaLines(all.length).map(escape).join('\n');

      // Ligne TOTAL en bas du tableau (séparée par une ligne vide pour la lisibilité).
      const totalLine = escape(
        `TOTAL : ${totalMontant.toLocaleString('fr-FR')} FCFA  (${countMontant} opération${countMontant > 1 ? 's' : ''} avec montant)`
      );

      // Meta + ligne vide + tableau + ligne vide + total.
      // BOM UTF-8 ('\uFEFF') en tête : sans lui, Excel sur Mac/Windows ouvre le CSV en
      // MacRoman/Latin-1 et "Téléphone" devient "T√©l√©phone".
      const fullCsv = `${metaCsv}\n\n${csvData}\n${totalLine}`;
      const blob = new Blob(['\uFEFF', fullCsv], { type: 'text/csv;charset=utf-8;' });
      saveAs(blob, `operations-${moment().format('YYYY-MM-DD')}.csv`);
    } catch (e) {
      console.error('Erreur export CSV:', e);
    } finally {
      setExporting(false);
    }
  };

  const exportToPDF = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const all = await fetchAllForExport();

      // Force U+00A0 (no-break space) comme séparateur de milliers — empêche autoTable de wrapper
      // sur l'espace fin (U+202F) inséré par toLocaleString('fr-FR'), qui casse "1 000" en "1 0 0 0".
      const formatMontant = (n) => {
        if (n == null || n === '') return '';
        const v = Number(n);
        if (Number.isNaN(v)) return String(n);
        return v.toLocaleString('fr-FR').replace(/\s/g, '\u00A0');
      };

      const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
      const pageWidth = doc.internal.pageSize.width;

      // Titre centré
      doc.setFontSize(18);
      doc.setFont(undefined, 'bold');
      doc.text('Liste des opérations', pageWidth / 2, 14, { align: 'center' });
      doc.setFont(undefined, 'normal');

      // Sous-titre : on dessine segment par segment pour pouvoir colorer en ROUGE
      // les valeurs dynamiques (ville, période). Largeur totale calculée pour centrer.
      // Note: on évite les caractères Unicode hors Latin-1 (ex. → U+2192) qui s'affichent
      // comme glyphes corrompus avec la police helvetica par défaut de jsPDF.
      const COLOR_GREY = [80, 80, 80];
      const COLOR_RED = [200, 30, 30];
      const SEP = '   |   ';

      const segments = [{ text: `Total : ${all.length} opérations`, color: COLOR_GREY, bold: false }];
      if (cityFilter) {
        const ville = cities.find((c) => c.code === cityFilter)?.name || cityFilter;
        segments.push({ text: SEP, color: COLOR_GREY, bold: false });
        segments.push({ text: 'Ville : ', color: COLOR_GREY, bold: false });
        segments.push({ text: ville, color: COLOR_RED, bold: true });
      }
      if (types.length) {
        segments.push({ text: SEP, color: COLOR_GREY, bold: false });
        segments.push({ text: 'Types : ', color: COLOR_GREY, bold: false });
        segments.push({ text: types.map((t) => TYPE_LABEL[t] || t).join(', '), color: COLOR_RED, bold: true });
      }
      if (dateRange?.[0] && dateRange?.[1]) {
        segments.push({ text: SEP, color: COLOR_GREY, bold: false });
        segments.push({ text: 'Période : ', color: COLOR_GREY, bold: false });
        segments.push({
          text: `${dateRange[0].format('DD/MM/YYYY')} - ${dateRange[1].format('DD/MM/YYYY')}`,
          color: COLOR_RED,
          bold: true,
        });
      }
      if (searchTerm) {
        segments.push({ text: SEP, color: COLOR_GREY, bold: false });
        segments.push({ text: `Recherche : "${searchTerm}"`, color: COLOR_GREY, bold: false });
      }

      doc.setFontSize(11);
      // Calcul de la largeur totale pour centrer (en tenant compte des changements de fonte)
      let subtitleWidth = 0;
      for (const s of segments) {
        doc.setFont(undefined, s.bold ? 'bold' : 'normal');
        subtitleWidth += doc.getTextWidth(s.text);
      }
      let x = (pageWidth - subtitleWidth) / 2;
      const y = 22;
      for (const s of segments) {
        doc.setFont(undefined, s.bold ? 'bold' : 'normal');
        doc.setTextColor(s.color[0], s.color[1], s.color[2]);
        doc.text(s.text, x, y);
        x += doc.getTextWidth(s.text);
      }

      // Ligne 2 : date de génération, plus petite
      doc.setFont(undefined, 'normal');
      doc.setFontSize(9);
      doc.setTextColor(120);
      doc.text(`Généré le ${moment().format('DD/MM/YYYY HH:mm')}`, pageWidth / 2, 28, { align: 'center' });
      doc.setTextColor(0);

      // Calcul du total des montants (ignore les opérations sans montant)
      let pdfTotalMontant = 0;
      let pdfCountMontant = 0;
      for (const r of all) {
        if (typeof r.amount === 'number' && Number.isFinite(r.amount)) {
          pdfTotalMontant += r.amount;
          pdfCountMontant += 1;
        }
      }

      autoTable(doc, {
        startY: 32,
        theme: 'grid',
        styles: {
          fontSize: 7,
          cellPadding: 1.8,
          overflow: 'linebreak',
          valign: 'middle',
          lineWidth: 0.1,
          lineColor: [180, 180, 180],
        },
        headStyles: { fillColor: [25, 118, 210], textColor: 255, fontStyle: 'bold', halign: 'left', lineColor: [25, 118, 210] },
        alternateRowStyles: { fillColor: [245, 247, 250] },
        // 11 colonnes — somme des cellWidth ≤ 269 mm (A4 paysage − marges 2×14)
        columnStyles: {
          0:  { cellWidth: 24 },                                           // Type
          1:  { cellWidth: 24 },                                           // Ville
          2:  { cellWidth: 28 },                                           // Date
          3:  { cellWidth: 24, halign: 'right', overflow: 'visible' },     // Montant
          4:  { cellWidth: 28 },                                           // Téléphone
          5:  { cellWidth: 28 },                                           // SenderPhone
          6:  { cellWidth: 28 },                                           // ReceiverPhone
          7:  { cellWidth: 22 },                                           // Ancienne Immat.
          8:  { cellWidth: 22 },                                           // Nouvelle Immat.
          9:  { cellWidth: 20 },                                           // Ancien Num.
          10: { cellWidth: 20 },                                           // Nouveau Num.
        },
        head: [
          [
            'Type',
            'Ville',
            'Date',
            'Montant',
            'Téléphone',
            'SenderPhone',
            'ReceiverPhone',
            'Ancienne Immat.',
            'Nouvelle Immat.',
            'Ancien Num.',
            'Nouveau Num.',
          ],
        ],
        body: all.map((r) => [
          TYPE_LABEL[r.type] || r.type,
          r.ville?.nom ?? '',
          moment(r.date).format('DD/MM/YYYY HH:mm'),
          formatMontant(r.amount),
          r.phone ?? '',
          r.senderPhone ?? '',
          r.receiverPhone ?? '',
          r.meta?.ancienneImmatriculation ?? '',
          r.meta?.nouvelleImmatriculation ?? '',
          r.meta?.ancienNumero ?? '',
          r.meta?.nouveauNumero ?? '',
        ]),
        // Pied de tableau (11 colonnes) : libellé TOTAL fusionné sur les 3 premières colonnes,
        // valeur dans la colonne Montant (col 3), reste fusionné sur les 7 dernières colonnes.
        // Style fond bleu foncé / texte blanc = identifiable au premier coup d'œil.
        foot: [
          [
            {
              content: `TOTAL  (${pdfCountMontant} opération${pdfCountMontant > 1 ? 's' : ''} avec montant)`,
              colSpan: 3,
              styles: { halign: 'right' },
            },
            {
              content: `${pdfTotalMontant.toLocaleString('fr-FR').replace(/\s/g, '\u00A0')} FCFA`,
              styles: { halign: 'right', overflow: 'visible' },
            },
            { content: '', colSpan: 7 },
          ],
        ],
        footStyles: {
          fillColor: [13, 71, 161],
          textColor: 255,
          fontStyle: 'bold',
          fontSize: 9,
          lineColor: [13, 71, 161],
        },
        didDrawPage: (data) => {
          const pageCount = doc.internal.getNumberOfPages();
          const pageSize = doc.internal.pageSize;
          const pageHeight = pageSize.height || pageSize.getHeight();
          doc.setFontSize(8);
          doc.setTextColor(120);
          doc.text(
            `Page ${data.pageNumber} / ${pageCount}`,
            pageSize.width - 30,
            pageHeight - 6
          );
        },
      });
      doc.save(`operations-${moment().format('YYYY-MM-DD')}.pdf`);
    } catch (e) {
      console.error('Erreur export PDF:', e);
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
      <Breadcrumb separator={<RightOutlined />}>
        <Breadcrumb.Item>
          <Link to="/dashboard">Accueil</Link>
        </Breadcrumb.Item>
        <Breadcrumb.Item>
          <span>Transactions</span>
        </Breadcrumb.Item>
        <Breadcrumb.Item>
          <span>Opérations</span>
        </Breadcrumb.Item>
      </Breadcrumb>

      <div className="card">
        <div className="card-body">
                  {/* 🔎 Filtres — 100% width sur mobile, grille sur desktop */}
                  <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
                    <Col xs={24} md={12} lg={8}>
                      <Input
                        size={controlSize}
                        placeholder="Rechercher (téléphone, contractorId...)"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        allowClear
                      />
                    </Col>
                    <Col xs={24} md={12} lg={7}>
                      <Select
                        mode="multiple"
                        size={controlSize}
                        placeholder="Filtrer par type"
                        value={types}
                        onChange={setTypes}
                        style={{ width: '100%' }}
                        allowClear
                      >
                        {Object.entries(TYPE_LABEL).map(([k, v]) => (
                          <Option key={k} value={k}>
                            {v}
                          </Option>
                        ))}
                      </Select>
                    </Col>
                    <Col xs={24} md={12} lg={5}>
                      <Select
                        size={controlSize}
                        allowClear
                        showSearch
                        placeholder={
                          <span>
                            <EnvironmentOutlined style={{ marginRight: 6 }} />
                            Filtrer par ville
                          </span>
                        }
                        value={cityFilter}
                        onChange={setCityFilter}
                        style={{ width: '100%' }}
                        optionFilterProp="label"
                        options={cities.map(c => ({ value: c.code, label: c.name }))}
                      />
                    </Col>
                    <Col xs={24} md={12} lg={4}>
                      <RangePicker
                        size={controlSize}
                        value={dateRange}
                        onChange={setDateRange}
                        format="DD/MM/YYYY"
                        allowClear
                        style={{ width: '100%' }}
                      />
                    </Col>
                  </Row>

                  {/* ⬇️ Actions d’export — wrap + plein largeur en mobile */}
                  <Row gutter={[8, 8]} style={{ marginBottom: 16 }}>
                    <Col xs={24} sm={12} md={8} lg={6}>
                      <Button
                        block={isMobile}
                        size={controlSize}
                        icon={<FileExcelOutlined />}
                        onClick={exportToExcel}
                        type="primary"
                        loading={exporting}
                        disabled={exporting}
                      >
                        Exporter Excel (.xlsx)
                      </Button>
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6}>
                      <Button
                        block={isMobile}
                        size={controlSize}
                        icon={<DownloadOutlined />}
                        onClick={exportToCSV}
                        loading={exporting}
                        disabled={exporting}
                      >
                        Exporter CSV
                      </Button>
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6}>
                      <Button
                        block={isMobile}
                        size={controlSize}
                        icon={<FilePdfOutlined />}
                        onClick={exportToPDF}
                        danger
                        loading={exporting}
                        disabled={exporting}
                      >
                        Exporter PDF
                      </Button>
                    </Col>
                  </Row>

                  {/* Tableau */}
                  <Table
                    rowKey="_id"
                    loading={isLoading}
                    columns={columns}
                    dataSource={rows}
                    size={isMobile ? 'small' : 'middle'}
                    pagination={{
                      current: page,
                      pageSize: limit,
                      total,
                      showSizeChanger: !isMobile,
                      pageSizeOptions: ['10', '20', '50', '100'],
                      showTotal: (t) => `${t} opérations`,
                      responsive: true,
                    }}
                    onChange={onChangeTable}
                    scroll={{ x: true }} // ✅ évite les débordements sur petit écran
                  />
        </div>
      </div>
    </>
  );
};

export default Operations;
